if (!customElements.get('armada-moving-teasers')) {
  class ArmadaMovingTeasers extends HTMLElement {
    constructor() {
      super();
      this._tick = this._tick.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onReducedMotionChange = this._onReducedMotionChange.bind(this);
      this._onMobileChange = this._onMobileChange.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-moving-teasers-viewport]');
      this.track = this.querySelector('[data-moving-teasers-track]');
      this.firstSet = this.querySelector('[data-moving-teasers-set]');
      this.items = Array.from(this.querySelectorAll('.armada-moving-teasers__item'));
      this.videos = Array.from(this.querySelectorAll('video'));

      if (!this.viewport || !this.track || !this.firstSet) return;

      this.speedMs = (parseInt(this.dataset.autoplaySpeed, 10) || 28) * 1000;
      this.focusDurationMs = (parseInt(this.dataset.focusDuration, 10) || 3) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      // Mobile breakpoint mirrors the project's desktop breakpoint (990px).
      // Below it, teaser sentences are wider than the viewport, so the
      // desktop focal-point choreography has nothing to anchor to. We swap
      // to a simpler, fully continuous flow.
      this.mobileMq = window.matchMedia('(max-width: 989px)');
      this.isMobile = this.mobileMq.matches;

      this.isVisible = false;
      this.isPlaying = false;
      this.animationFrame = null;
      this.lastTime = 0;
      this.lastVideoSync = 0;
      this.resumeStartedAt = 0;
      this.loopWidth = 0;
      // Track-relative scroll offset, in CSS px. The track is translated by
      // -offset, so increasing offset slides items leftward through the
      // viewport. Autoplay and drag both write to this single variable, so
      // dragging while autoplay is running cleanly composites the two.
      this.offset = 0;
      this.dragging = false;
      this.dragMoved = false;
      this.dragStartX = 0;
      this.lastDragX = 0;

      this._prepareVideos();
      this._measure();
      this._applyOffset();
      this._updateMotionState();
      this._bindEvents();
      this._observe();

      if (this.reducedMotion.matches) this._pauseVideos();
    }

    disconnectedCallback() {
      this._stop();
      if (!this.viewport) return;

      this.viewport.removeEventListener('pointerdown', this._onPointerDown);
      this.viewport.removeEventListener('click', this._onClick, true);
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup', this._onPointerUp);
      document.removeEventListener('pointercancel', this._onPointerUp);

      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.reducedMotion) {
        this.reducedMotion.removeEventListener('change', this._onReducedMotionChange);
      }
      if (this.mobileMq) {
        this.mobileMq.removeEventListener('change', this._onMobileChange);
      }
    }

    _bindEvents() {
      this.viewport.addEventListener('pointerdown', this._onPointerDown);
      this.viewport.addEventListener('click', this._onClick, true);
      document.addEventListener('pointermove', this._onPointerMove, { passive: false });
      document.addEventListener('pointerup', this._onPointerUp);
      document.addEventListener('pointercancel', this._onPointerUp);
      this.reducedMotion.addEventListener('change', this._onReducedMotionChange);
      this.mobileMq.addEventListener('change', this._onMobileChange);
    }

    _observe() {
      this.visibilityObserver = new IntersectionObserver(this._onVisibilityChange, {
        threshold: 0.1
      });
      this.visibilityObserver.observe(this);

      this.resizeObserver = new ResizeObserver(() => this._measure());
      this.resizeObserver.observe(this.firstSet);
      this.resizeObserver.observe(this.viewport);
    }

    _onVisibilityChange(entries) {
      entries.forEach((entry) => {
        this.isVisible = entry.isIntersecting;
        if (this.isVisible) {
          this._start();
          this._syncVideos(true);
        } else {
          this._stop();
          this._pauseVideos();
        }
      });
    }

    _onReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this._stop();
        this._pauseVideos();
      } else if (this.isVisible) {
        this._start();
        this._syncVideos(true);
      }
    }

    _onMobileChange(event) {
      this.isMobile = event.matches;
      if (this.isMobile) this._resetItemMotion();
      this._updateMotionState();
      this._syncVideos(true);
    }

    _measure() {
      const trackStyles = window.getComputedStyle(this.track);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      this.loopWidth = this.firstSet.getBoundingClientRect().width + gap;

      if (this.loopWidth > 0) {
        this.offset = this._wrap(this.offset);
      }

      this._applyOffset();
      this._updateMotionState();
    }

    _wrap(value) {
      if (this.loopWidth <= 0) return value;
      let wrapped = value % this.loopWidth;
      if (wrapped < 0) wrapped += this.loopWidth;
      return wrapped;
    }

    _applyOffset() {
      this.track.style.setProperty('--track-x', `${(-this.offset).toFixed(2)}px`);
    }

    _tick(currentTime) {
      if (!this.isPlaying) return;
      if (!this.lastTime) this.lastTime = currentTime;

      const delta = Math.min(currentTime - this.lastTime, 40);
      this.lastTime = currentTime;

      const closestDistance = this.isMobile ? 0 : this._updateMotionState();

      if (this.loopWidth > 0) {
        const baseVelocity = this.loopWidth / this.speedMs;
        const rampElapsed = currentTime - this.resumeStartedAt;
        const ramp = this._easeOutQuint(Math.min(rampElapsed / 700, 1));
        const multiplier = this._velocityMultiplier(closestDistance);
        const distance = baseVelocity * delta * ramp * multiplier;

        this.offset = this._wrap(this.offset + distance);
        this._applyOffset();
      }

      if (currentTime - this.lastVideoSync > 400) {
        this._syncVideos();
        this.lastVideoSync = currentTime;
      }

      this.animationFrame = requestAnimationFrame(this._tick);
    }

    _start() {
      if (this.reducedMotion.matches || !this.isVisible || this.loopWidth <= 0) return;
      if (this.isPlaying) return;

      this.isPlaying = true;
      this.lastTime = 0;
      this.resumeStartedAt = performance.now();
      this.animationFrame = requestAnimationFrame(this._tick);
    }

    _stop() {
      this.isPlaying = false;
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      this.lastTime = 0;
    }

    _onPointerDown(event) {
      if (event.button !== 0) return;

      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.lastDragX = event.clientX;
      this.viewport.classList.add('is-dragging');
      // Autoplay keeps running. Drag is layered on top via incremental deltas.
    }

    _onPointerMove(event) {
      if (!this.dragging) return;

      const totalDelta = event.clientX - this.dragStartX;
      if (Math.abs(totalDelta) > 4) this.dragMoved = true;

      // Apply the incremental delta since the last move so autoplay's own
      // per-frame contribution is preserved between move events.
      const incrementalDelta = event.clientX - this.lastDragX;
      this.lastDragX = event.clientX;

      if (!this.dragMoved || incrementalDelta === 0) return;

      event.preventDefault();
      this.offset = this._wrap(this.offset - incrementalDelta);
      this._applyOffset();
      this._updateMotionState();
    }

    _onPointerUp() {
      if (!this.dragging) return;

      this.dragging = false;
      this.viewport.classList.remove('is-dragging');
    }

    _onClick(event) {
      if (!this.dragMoved) return;

      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = false;
    }

    _prepareVideos() {
      this.videos.forEach((video) => {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'metadata';
      });
    }

    _syncVideos(force = false) {
      if (!force && this.reducedMotion.matches) return;

      this.videos.forEach((video) => {
        if (this._videoShouldPlay(video)) {
          if (!video.paused) return;
          video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    _pauseVideos() {
      this.videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    _videoShouldPlay(video) {
      if (this.reducedMotion.matches || !this.isVisible || !this.viewport) return false;
      if (video.getClientRects().length === 0) return false;

      const videoRect = video.getBoundingClientRect();
      const viewportRect = this.viewport.getBoundingClientRect();

      return (
        videoRect.right > viewportRect.left &&
        videoRect.left < viewportRect.right &&
        videoRect.bottom > viewportRect.top &&
        videoRect.top < viewportRect.bottom
      );
    }

    _updateMotionState() {
      if (!this.viewport || this.items.length === 0) return 0;
      if (this.isMobile) return 0;

      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportWidth = viewportRect.width || 1;
      const viewportCenter = viewportRect.left + viewportWidth / 2;
      let closestDistance = Infinity;

      this.items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const signedDistance = (itemCenter - viewportCenter) / viewportWidth;

        if (Math.abs(signedDistance) < Math.abs(closestDistance)) {
          closestDistance = signedDistance;
        }

        const position = (itemCenter - viewportRect.left) / viewportWidth;
        const offsetFromCenter = position - 0.5;
        const absOffset = Math.abs(offsetFromCenter);

        // Spotlight focus: peaks dead-center, falls off toward the edges.
        const focus = 1 - this._smoothstep(0.04, 0.42, absOffset);
        // Hard fade beyond the rails so cards visibly enter from the right and exit to the left.
        const rightFade = 1 - this._smoothstep(0.55, 1.05, position);
        const leftFade = this._smoothstep(-0.05, 0.45, position);
        const opacity = this._clamp(Math.min(rightFade, leftFade), 0, 1);

        // Horizontal drift. Entering items (right) gain a small, linear lag
        // so they appear to "settle" into focus. Exiting items (left) ramp
        // with ease-in cubic so they look subtle just past center and then
        // accelerate hard toward the edge — a snap-out exit, not a glide.
        let teaserX = 0;
        if (offsetFromCenter > 0.18) {
          teaserX = (offsetFromCenter - 0.18) * 2.6;
        } else if (offsetFromCenter < -0.18) {
          const exitProgress = this._clamp((-offsetFromCenter - 0.18) / 0.35, 0, 1);
          teaserX = -this._easeInCubic(exitProgress) * 6.0;
        }

        // Wider scale range: visibly shrink while fading, full size at center.
        // 0.74 → 1.00 reads clearly without distorting layout-critical text.
        const teaserScale = 0.74 + focus * 0.26;
        // Subtle counter-zoom on the media so the image gently pulls in
        // toward its natural framing as the card reaches center.
        const mediaScale = 1.06 - focus * 0.06;

        item.style.setProperty('--teaser-opacity', opacity.toFixed(3));
        item.style.setProperty('--teaser-x', `${teaserX.toFixed(2)}rem`);
        item.style.setProperty('--teaser-scale', teaserScale.toFixed(3));
        item.style.setProperty('--media-scale', mediaScale.toFixed(3));
      });

      return closestDistance === Infinity ? 0 : closestDistance;
    }

    _resetItemMotion() {
      this.items.forEach((item) => {
        item.style.removeProperty('--teaser-opacity');
        item.style.removeProperty('--teaser-x');
        item.style.removeProperty('--teaser-scale');
        item.style.removeProperty('--media-scale');
      });
    }

    _velocityMultiplier(closestDistance) {
      // Mobile: constant pace. No center hold, no left kick — a single,
      // continuous glide so long sentences read as a marquee rather than a
      // series of focal moments.
      if (this.isMobile) return 1;

      const absDistance = Math.abs(closestDistance);
      // Width of the slow band scales with the focus-duration setting:
      // 1s → ~0.04, 3s → ~0.11, 6s → ~0.21 (fraction of viewport width).
      const focusBandRatio = this._clamp(this.focusDurationMs / 28000, 0.035, 0.21);

      const centerHold = 1 - this._smoothstep(0, focusBandRatio, absDistance);
      const accelOff = this._smoothstep(focusBandRatio * 0.65, 0.42, absDistance);

      // Asymmetric kick so cards rip toward the left edge after passing
      // center. Ease-in cubic gives the perceptual feel of acceleration:
      // gentle just past center, then a hard burst toward the edge.
      let leftKick = 0;
      if (closestDistance < -0.04) {
        const exitStart = focusBandRatio * 0.5;
        const exitEnd = 0.4;
        const exitProgress = this._clamp(
          (absDistance - exitStart) / (exitEnd - exitStart),
          0,
          1
        );
        leftKick = this._easeInCubic(exitProgress) * 1.6;
      }

      const multiplier = 1.10 + accelOff * 2.0 + leftKick - centerHold * 0.78;
      return this._clamp(multiplier, 0.32, 4.5);
    }

    _easeOutQuint(value) {
      return 1 - Math.pow(1 - value, 5);
    }

    _easeInCubic(value) {
      return value * value * value;
    }

    _smoothstep(edge0, edge1, value) {
      const x = this._clamp((value - edge0) / (edge1 - edge0), 0, 1);
      return x * x * (3 - 2 * x);
    }

    _clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }
  }

  customElements.define('armada-moving-teasers', ArmadaMovingTeasers);
}
